import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, PhoneIncoming, RefreshCw, ChevronRight,
  UserPlus, Link2, Trash2, Clock, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

// AI Calls Inbox — landing zone for Retell AI-answered inbound calls
// whose caller phone isn't yet a lead in the CRM. Rep reviews the AI's
// captured facts, then converts to a full lead, links to an existing
// one, or dismisses as spam. Backend: /api/ai-call-inbox.
const STATUSES = [
  { key: "new",       label: "New",        tone: "bg-amber-50 border-amber-300 text-amber-800" },
  { key: "created",   label: "Converted",  tone: "bg-emerald-50 border-emerald-300 text-emerald-800" },
  { key: "linked",    label: "Linked",     tone: "bg-sky-50 border-sky-300 text-sky-800" },
  { key: "dismissed", label: "Dismissed",  tone: "bg-slate-100 border-slate-300 text-slate-600" },
];

function relTime(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmtDur(ms) {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
}

export default function AICallInbox() {
  const [rows, setRows]           = useState([]);
  const [status, setStatus]       = useState("new");
  const [busy, setBusy]           = useState(false);
  const [filter, setFilter]       = useState("");
  const [active, setActive]       = useState(null);  // row being reviewed in the drawer

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/ai-call-inbox", { params: { status } });
      setRows(data?.rows || []);
    } catch {
      setRows([]);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [status]);
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [status]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) =>
      !q ||
      (r.from_number || "").includes(q) ||
      (r.summary || "").toLowerCase().includes(q) ||
      ((r.extracted?.property_address) || "").toLowerCase().includes(q),
    );
  }, [rows, filter]);

  return (
    <div className="p-6 space-y-4" data-testid="ai-call-inbox-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Sparkles className="text-fuchsia-500" size={24} />
            AI Calls Inbox
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            Unknown-caller inbounds handled by the Retell AI agent. Review the AI transcript, then convert to a lead or dismiss as spam.
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={load} disabled={busy}
          data-testid="ai-call-inbox-refresh"
          className="rounded-full"
        >
          <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 items-center">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatus(s.key)}
            data-testid={`ai-call-inbox-status-${s.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 transition ${
              status === s.key
                ? "bg-slate-900 border-slate-900 text-white"
                : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
            }`}
          >
            {s.label}
          </button>
        ))}
        <Input
          placeholder="Filter phone, address, summary…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="ml-auto h-8 w-72 text-xs"
          data-testid="ai-call-inbox-filter"
        />
      </div>

      {/* Table */}
      <div className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50 px-4 py-2.5 border-b">
          <div className="col-span-3">Caller / Property</div>
          <div className="col-span-2">Received</div>
          <div className="col-span-1">Duration</div>
          <div className="col-span-1 text-center">Score</div>
          <div className="col-span-3">AI Summary</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {filtered.length === 0 && !busy && (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            {rows.length === 0
              ? "No inbound callbacks yet. When someone calls back one of your 20 numbers, the AI-captured script and motivation score land here for review."
              : "No calls match your filter."}
          </div>
        )}
        {filtered.map((r) => (
          <div
            key={r.id}
            className="grid grid-cols-12 items-center px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer transition"
            onClick={() => setActive(r)}
            data-testid={`ai-call-inbox-row-${r.id}`}
          >
            <div className="col-span-3">
              <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                <PhoneIncoming size={13} className="text-fuchsia-500 shrink-0" />
                {r.from_number || "Unknown"}
                {r.has_linked_lead && (
                  <span
                    title={r.linked_lead_name ? `Callback from existing lead: ${r.linked_lead_name}` : "Callback from existing CRM lead"}
                    className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border border-sky-300 bg-sky-50 text-sky-700"
                    data-testid={`ai-call-inbox-row-${r.id}-lead-badge`}
                  >
                    Lead
                  </span>
                )}
                {r.kind === "missed_ai_callback" && (
                  <span
                    title="Retell was unreachable — seller was routed to voicemail"
                    className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-700"
                  >
                    Missed AI
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {r.extracted?.property_address
                  || r.linked_lead_name
                  || <span className="italic">no property captured</span>}
              </div>
            </div>
            <div className="col-span-2 text-xs text-slate-700 flex items-center gap-1">
              <Clock size={11} className="text-muted-foreground" />
              {relTime(r.created_at)}
            </div>
            <div className="col-span-1 text-xs text-slate-700">{fmtDur(r.duration_ms)}</div>
            <div className="col-span-1 flex items-center justify-center">
              <ScoreBadge score={r.motivation_score} testid={`ai-call-inbox-row-${r.id}-score`} />
            </div>
            <div className="col-span-3 text-xs text-slate-700 truncate" title={r.summary || ""}>
              {r.summary || <span className="text-muted-foreground italic">no AI summary</span>}
            </div>
            <div className="col-span-2 flex items-center justify-end">
              <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-0.5">
                Review <ChevronRight size={13} />
              </span>
            </div>
          </div>
        ))}
      </div>

      <ReviewDrawer row={active} onClose={(refetch) => { setActive(null); if (refetch) load(); }} />
    </div>
  );
}


// ============================ REVIEW DRAWER ============================
function ReviewDrawer({ row, onClose }) {
  const [name,    setName]    = useState("");
  const [addr,    setAddr]    = useState("");
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    if (!row) return;
    setName(row.extracted?.seller_name || "");
    setAddr(row.extracted?.property_address || "");
  }, [row]);

  if (!row) return null;

  const extracted = row.extracted || {};

  const createLead = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ai-call-inbox/${row.id}/create-lead`, {
        name: name.trim() || null,
        property_address: addr.trim() || null,
      });
      toast.success("Lead created. Now visible in Pipeline + AI Lead Manager.");
      onClose(true);
      // Follow-through: give the rep a quick jump into the new lead
      if (data?.lead_id) {
        window.location.href = `/boards?lead_id=${data.lead_id}`;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm("Mark this AI call as reviewed / not a real seller?")) return;
    setBusy(true);
    try {
      await api.post(`/ai-call-inbox/${row.id}/dismiss`, {});
      toast.success("Dismissed.");
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ai-call-inbox-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneIncoming className="text-fuchsia-500" size={20} />
            AI Call from {row.from_number || "unknown"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Score + linked-lead strip */}
          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="flex items-center gap-2 rounded-lg border-2 border-fuchsia-200 bg-fuchsia-50 px-3 py-1.5"
              data-testid="ai-call-inbox-drawer-score"
            >
              <span className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold">Motivation</span>
              <ScoreBadge score={row.motivation_score} testid="ai-call-inbox-drawer-score-badge" />
            </div>
            {row.has_linked_lead && row.linked_lead_id && (
              <a
                href={`/boards?lead_id=${row.linked_lead_id}`}
                className="inline-flex items-center gap-1 rounded-lg border-2 border-sky-300 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-100 transition"
                data-testid="ai-call-inbox-drawer-open-lead"
              >
                <Link2 size={12} />
                Open existing lead{row.linked_lead_name ? ` — ${row.linked_lead_name}` : ""}
              </a>
            )}
            {row.kind === "missed_ai_callback" && (
              <span className="inline-flex items-center gap-1 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">
                <Clock size={12} />
                Missed AI — routed to voicemail
              </span>
            )}
          </div>

          {/* Fact strip */}
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <FactCell label="Duration" value={fmtDur(row.duration_ms)} />
            <FactCell label="Received" value={relTime(row.created_at)} />
            <FactCell label="Outcome" value={row.outcome || "—"} />
            <FactCell label="Sentiment" value={row.sentiment || "—"} />
            <FactCell label="Asking Price" value={extracted.asking_price || "—"} />
            <FactCell label="Timeline" value={extracted.timeline || "—"} />
            <FactCell label="Condition" value={extracted.condition || "—"} />
            <FactCell label="Occupancy" value={extracted.occupancy || "—"} />
            <FactCell label="Interested" value={row.seller_interested === true ? "Yes" : row.seller_interested === false ? "No" : "—"} />
          </div>

          {/* Summary */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">AI Summary / Script Notes</div>
            <div className="rounded-lg border bg-slate-50 p-3 text-slate-800 whitespace-pre-wrap">
              {row.summary || <span className="text-muted-foreground italic">No summary generated</span>}
            </div>
          </div>

          {/* Transcript */}
          {(row.transcript || (row.transcript_object || []).length > 0) && (
            <details className="rounded-lg border bg-white">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <MessageCircle size={13} /> View transcript
              </summary>
              <div className="max-h-64 overflow-y-auto px-3 py-2 space-y-1 text-xs">
                {(row.transcript_object || []).length > 0 ? (
                  row.transcript_object.map((t, i) => (
                    <div key={i} className={t.role === "agent" ? "text-slate-700" : "text-fuchsia-700 font-medium"}>
                      <span className="uppercase text-[10px] tracking-widest text-slate-400 mr-1.5">{t.role}</span>
                      {t.content}
                    </div>
                  ))
                ) : (
                  <pre className="whitespace-pre-wrap font-sans">{row.transcript}</pre>
                )}
              </div>
            </details>
          )}

          {/* Recording */}
          {row.recording_url && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Recording</div>
              <audio controls src={row.recording_url} className="w-full h-8" />
            </div>
          )}

          {/* Lead-creation form */}
          {row.status === "new" && (
            <div className="rounded-lg border-2 border-fuchsia-200 bg-fuchsia-50/40 p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold flex items-center gap-1">
                <UserPlus size={12} /> Convert to Lead
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Seller Name</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="AI Inbound Caller"
                    className="h-8 text-xs"
                    data-testid="ai-call-inbox-name-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Property Address</label>
                  <Input
                    value={addr}
                    onChange={(e) => setAddr(e.target.value)}
                    placeholder="123 Main St, Cape Coral, FL"
                    className="h-8 text-xs"
                    data-testid="ai-call-inbox-addr-input"
                  />
                </div>
              </div>
              <div className="text-[10px] text-fuchsia-700/80">
                Creates a new lead with phone <b>{row.from_number || "unknown"}</b>, tags it <b>AI Voice Inbound</b>, and back-links this AI transcript to the lead&apos;s timeline.
              </div>
            </div>
          )}

          {row.status !== "new" && (
            <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              Row status: <b>{row.status}</b>{row.linked_lead_id ? ` · lead ${row.linked_lead_id.slice(0, 8)}…` : ""}.
              {row.status === "created" && row.linked_lead_id && (
                <> <a href={`/boards?lead_id=${row.linked_lead_id}`} className="text-fuchsia-600 underline font-semibold">Open the lead</a>.</>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost" size="sm"
            onClick={() => onClose()}
            disabled={busy}
            data-testid="ai-call-inbox-close"
          >
            Close
          </Button>
          {row.status === "new" && (
            <>
              <Button
                variant="outline" size="sm"
                onClick={dismiss}
                disabled={busy}
                data-testid="ai-call-inbox-dismiss"
                className="text-slate-600 border-slate-300"
              >
                <Trash2 size={13} className="mr-1" /> Dismiss
              </Button>
              <Button
                size="sm"
                onClick={createLead}
                disabled={busy}
                data-testid="ai-call-inbox-create-lead"
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              >
                <UserPlus size={13} className="mr-1" /> Create Lead
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FactCell({ label, value }) {
  return (
    <div className="rounded-md border bg-white px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className="text-xs font-medium text-slate-800 truncate" title={value}>{value}</div>
    </div>
  );
}

// Motivation score chip — 0-100 scale, colour-graded so reps can
// eyeball the hot callbacks at a glance without reading the summary.
function ScoreBadge({ score, testid }) {
  if (score === null || score === undefined) {
    return <span className="text-xs text-muted-foreground" data-testid={testid}>—</span>;
  }
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  let tone = "bg-slate-100 text-slate-600 border-slate-200";
  if (n >= 80)      tone = "bg-emerald-100 text-emerald-800 border-emerald-300";
  else if (n >= 60) tone = "bg-amber-100 text-amber-800 border-amber-300";
  else if (n >= 40) tone = "bg-sky-100 text-sky-800 border-sky-300";
  else              tone = "bg-slate-100 text-slate-600 border-slate-300";
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 rounded-full text-[11px] font-bold border ${tone}`}
      data-testid={testid}
      title={`Motivation score: ${n}/100`}
    >
      {n}
    </span>
  );
}
